import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile, getAuth } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import RequirePermission from '../../components/RequirePermission';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'super_admin' | 'admin' | 'annotator' | 'qc';
  permissions: {
    tasks: boolean;
    approvals: boolean;
    analytics: boolean;
    rejectionAnalytics: boolean;
    labeling: {
      queue: boolean;
      schemas: boolean;
      workbench: boolean;
      review: boolean;
      models: boolean;
      datasets: boolean;
      activeLearning: boolean;
      modelQA: boolean;
      referenceImages: boolean;
    };
  admin: {
    workforce: boolean;
    userManagement: boolean;
  };
  };
  createdAt: Date;
  updatedAt: Date;
}

const defaultPermissions = {
  tasks: false,
  approvals: false,
  analytics: false,
  rejectionAnalytics: false,
  labeling: {
    queue: false,
    schemas: false,
    workbench: false,
    review: false,
    models: false,
    datasets: false,
    activeLearning: false,
    modelQA: false,
    referenceImages: false,
  },
  admin: {
    workforce: false,
    userManagement: false,
  },
};

export default function UserManagement() {
  return (
    <RequirePermission permission="admin.userManagement">
      <UserManagementContent />
    </RequirePermission>
  );
}

function UserManagementContent() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'annotator' as User['role'],
    permissions: { ...defaultPermissions },
  });

  useEffect(() => {
    document.title = 'User Management — Pixano';
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersQuery = query(collection(db, 'adminUsers'));
      const snapshot = await getDocs(usersQuery);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as User[];
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        const userRef = doc(db, 'adminUsers', editingUser.id);
        
        // If user is super_admin, set all permissions to true
        const userData = {
          ...formData,
          updatedAt: new Date(),
        };
        
        if (formData.role === 'super_admin') {
          userData.permissions = {
            tasks: true,
            approvals: true,
            analytics: true,
            rejectionAnalytics: true,
            labeling: {
              queue: true,
              schemas: true,
              workbench: true,
              review: true,
              models: true,
              datasets: true,
              activeLearning: true,
              modelQA: true,
              referenceImages: true,
            },
            admin: {
              workforce: true,
              userManagement: true,
            },
          };
        }
        
        await updateDoc(userRef, userData);
        setUsers(users.map(user => 
          user.id === editingUser.id 
            ? { ...user, ...userData }
            : user
        ));
      } else {
        // Create new user - Note: This requires admin privileges to create users
        // For now, we'll just create the user document without Firebase Auth user
        // The admin will need to manually create the Firebase Auth user
        const newUser = {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        // If user is super_admin, set all permissions to true
        if (formData.role === 'super_admin') {
          newUser.permissions = {
            tasks: true,
            approvals: true,
            analytics: true,
            rejectionAnalytics: true,
            labeling: {
              queue: true,
              schemas: true,
              workbench: true,
              review: true,
              models: true,
              datasets: true,
              activeLearning: true,
              modelQA: true,
              referenceImages: true,
            },
            admin: {
              workforce: true,
              userManagement: true,
            },
          };
        }

        const docRef = await addDoc(collection(db, 'adminUsers'), newUser);
        setUsers([...users, { id: docRef.id, ...newUser }]);
        
        alert(`User created successfully! Please manually create a Firebase Auth user for ${formData.email} with a temporary password.`);
      }

      setShowAddModal(false);
      setEditingUser(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'annotator',
        permissions: { ...defaultPermissions },
      });
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error saving user. Please try again.');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      permissions: user.permissions,
    });
    setShowAddModal(true);
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'adminUsers', userId));
        setUsers(users.filter(user => user.id !== userId));
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user. Please try again.');
      }
    }
  };

  const updatePermission = (path: string, value: boolean) => {
    const keys = path.split('.');
    const newPermissions = { ...formData.permissions };
    
    if (keys.length === 1) {
      (newPermissions as any)[keys[0]] = value;
    } else if (keys.length === 2) {
      (newPermissions as any)[keys[0]][keys[1]] = value;
    }
    
    setFormData({ ...formData, permissions: newPermissions });
  };

  if (loading) {
    return <div className="p-8">Loading users...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Management</h1>
        <button
          onClick={() => {
            setEditingUser(null);
            setFormData({
              email: '',
              firstName: '',
              lastName: '',
              role: 'annotator',
              permissions: { ...defaultPermissions },
            });
            setShowAddModal(true);
          }}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Add User
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {user.firstName || user.lastName 
                    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                    : 'N/A'
                  }
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                    user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                    user.role === 'qc' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {(user.role || 'admin').replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.createdAt.toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(user)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="annotator">Annotator</option>
                    <option value="qc">Quality Control</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                {/* Permissions Section */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Permissions
                    {formData.role === 'super_admin' && (
                      <span className="text-sm text-gray-500 ml-2">(Super Admin has all permissions)</span>
                    )}
                  </h4>
                  
                  {/* Main Pages */}
                  <div className="space-y-2 mb-4">
                    <h5 className="text-sm font-medium text-gray-700">Main Pages</h5>
                    {[
                      { key: 'tasks', label: 'Tasks Overview' },
                      { key: 'approvals', label: 'Task Approvals' },
                      { key: 'analytics', label: 'Task Data Analytics' },
                      { key: 'rejectionAnalytics', label: 'Rejection Analytics' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.role === 'super_admin' || (formData.permissions[key as keyof typeof formData.permissions] as boolean)}
                          onChange={(e) => updatePermission(key, e.target.checked)}
                          disabled={formData.role === 'super_admin'}
                          className="mr-2"
                        />
                        {label}
                      </label>
                    ))}
                  </div>

                  {/* Labeling Section */}
                  <div className="space-y-2 mb-4">
                    <h5 className="text-sm font-medium text-gray-700">Labeling</h5>
                    {[
                      { key: 'queue', label: 'Labeling Queue' },
                      { key: 'schemas', label: 'Detection Setup' },
                      { key: 'workbench', label: 'Workbench' },
                      { key: 'review', label: 'Review' },
                      { key: 'models', label: 'Models' },
                      { key: 'datasets', label: 'Datasets' },
                      { key: 'activeLearning', label: 'Active Learning' },
                      { key: 'modelQA', label: 'Model & QA' },
                      { key: 'referenceImages', label: 'Reference Images' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.role === 'super_admin' || (formData.permissions.labeling[key as keyof typeof formData.permissions.labeling] as boolean)}
                          onChange={(e) => updatePermission(`labeling.${key}`, e.target.checked)}
                          disabled={formData.role === 'super_admin'}
                          className="mr-2"
                        />
                        {label}
                      </label>
                    ))}
                  </div>

                  {/* Admin Section */}
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-gray-700">Admin</h5>
                    {[
                      { key: 'workforce', label: 'Workforce' },
                      { key: 'userManagement', label: 'User Management' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.role === 'super_admin' || (formData.permissions.admin[key as keyof typeof formData.permissions.admin] as boolean)}
                          onChange={(e) => updatePermission(`admin.${key}`, e.target.checked)}
                          disabled={formData.role === 'super_admin'}
                          className="mr-2"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


